import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';
import { SERVICE_CATEGORIES } from '../../../config/ieumConfig';
import Spinner from '../../../components/common/Spinner';

const buildCategoryLabel = (sr) => {
  const services = Array.isArray(sr.services) ? sr.services : null;
  if (services && services.length > 0) {
    const first = SERVICE_CATEGORIES[services[0]]?.label || services[0];
    return services.length === 1 ? first : `${first} 외 ${services.length - 1}건`;
  }
  const k = sr.category || sr.service_category || '';
  return SERVICE_CATEGORIES[k]?.label || k || '-';
};

const STATUS_KEYS = [
  { label: '상담DB', key: 'consult_db' },
  { label: '상담', key: 'consult' },
  { label: '접수', key: 'received' },
  { label: '진행', key: 'in_progress' },
  { label: '보류', key: 'on_hold' },
  { label: '취소', key: 'cancelled' },
  { label: '완료', key: 'completed' },
];

const STATUS_LABEL_MAP = {
  consulting: '상담', consult: '상담',
  unread: '접수', read: '접수', assigned: '접수', received: '접수',
  reserved: '진행', visiting: '진행', in_progress: '진행',
  pending: '보류', on_hold: '보류', hold: '보류',
  cancelled: '취소', canceled: '취소',
  completed: '완료', done: '완료',
};

const TABLE_HEADERS = ['일시', '고객명', '휴대번호', '추적현황'];

const formatPhone = (p) => {
  const s = (p || '').replace(/\D/g, '');
  if (s.length === 11) return `${s.slice(0, 3)}-${s.slice(3, 7)}-${s.slice(7)}`;
  if (s.length === 10) return `${s.slice(0, 3)}-${s.slice(3, 6)}-${s.slice(6)}`;
  return p || '-';
};

const fmt = (ts) => {
  if (!ts) return '-';
  let d;
  if (ts.toDate) d = ts.toDate();
  else if (ts.seconds) d = new Date(ts.seconds * 1000);
  else d = new Date(ts);
  if (isNaN(d.getTime())) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
};

const CustomerTrackHistory = () => {
  const { userData } = useAuth();
  const [counts, setCounts] = useState(() => STATUS_KEYS.reduce((acc, s) => ({ ...acc, [s.key]: 0 }), {}));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.uid) return;
    setLoading(true);
    (async () => {
      try {
        const srSnap = await getDocs(query(
          collection(db, 'service_requests'),
          where('source_realtor_id', '==', userData.uid)
        ));
        const srs = srSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const c = STATUS_KEYS.reduce((acc, s) => ({ ...acc, [s.key]: 0 }), {});
        c.consult_db = srs.length;

        // assignments 조회
        const paSnap = await getDocs(collection(db, 'partner_assignments'));
        const paMap = {};
        paSnap.docs.forEach((d) => {
          const a = d.data();
          if (a.service_request_id) paMap[a.service_request_id] = a;
        });

        const tableRows = [];
        for (const sr of srs) {
          const pa = paMap[sr.id];
          const s = pa?.status || sr.hq_status || 'received';
          if (s === 'consulting' || s === 'consult') c.consult += 1;
          else if (s === 'unread' || s === 'read' || s === 'received' || s === 'assigned') c.received += 1;
          else if (s === 'reserved' || s === 'visiting' || s === 'in_progress') c.in_progress += 1;
          else if (s === 'pending' || s === 'on_hold' || s === 'hold') c.on_hold += 1;
          else if (s === 'cancelled' || s === 'canceled' || s === 'unmatched_cancelled') c.cancelled += 1;
          else if (s === 'completed' || s === 'done') c.completed += 1;

          // 고객 정보 조인
          let cust = null;
          if (sr.customer_id) {
            try {
              const cSnap = await getDocs(query(collection(db, 'customers'), where('__name__', '==', sr.customer_id)));
              if (!cSnap.empty) cust = cSnap.docs[0].data();
            } catch {}
          }

          tableRows.push({
            id: sr.id,
            date: fmt(sr.created_at || sr.createdAt),
            name: cust?.name || sr.name || '-',
            phone: cust?.phone || sr.phone || '-',
            category: buildCategoryLabel(sr),
            status: STATUS_LABEL_MAP[s] || s,
          });
        }

        tableRows.sort((a, b) => (a.date < b.date ? 1 : -1));
        setCounts(c);
        setRows(tableRows);
      } catch (e) { /* skip */ }
      finally { setLoading(false); }
    })();
  }, [userData?.uid]);

  if (loading) return <Spinner fullPage={false} />;

  return (
    <Container>
      <SectionTitle>고객 DB 추적</SectionTitle>
      <SectionDesc>상담부터 완료까지 전환 흐름을 실시간으로 파악합니다.</SectionDesc>

      <GridWrap>
        <StatusGrid>
          {STATUS_KEYS.slice(0, 3).map((s) => (
            <StatusCell key={s.label}>
              <CellLabel>{s.label}</CellLabel>
              <CellValue>{counts[s.key] || 0}</CellValue>
            </StatusCell>
          ))}
        </StatusGrid>
        <StatusGrid>
          {STATUS_KEYS.slice(3, 6).map((s) => (
            <StatusCell key={s.label}>
              <CellLabel>{s.label}</CellLabel>
              <CellValue>{counts[s.key] || 0}</CellValue>
            </StatusCell>
          ))}
        </StatusGrid>
        <StatusGrid style={{ gridTemplateColumns: '1fr 2fr' }}>
          <StatusCell>
            <CellLabel>{STATUS_KEYS[6].label}</CellLabel>
            <CellValue>{counts[STATUS_KEYS[6].key] || 0}</CellValue>
          </StatusCell>
          <div />
        </StatusGrid>
      </GridWrap>

      <TableHeader>
        <ThCell $flex={1}>{TABLE_HEADERS[0]}</ThCell>
        <ThCell $flex={0.7}>{TABLE_HEADERS[1]}</ThCell>
        <ThCell $flex={1.5}>{TABLE_HEADERS[2]}</ThCell>
        <ThCell $flex={0.7}>{TABLE_HEADERS[3]}</ThCell>
      </TableHeader>

      {rows.length === 0 ? (
        <EmptyBox>
          <EmptyIcon>
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="16" r="8" stroke="#D1D5DB" strokeWidth="2" fill="none" />
              <path d="M6 34c0-7.732 6.268-14 14-14s14 6.268 14 14" stroke="#D1D5DB" strokeWidth="2" fill="none" />
            </svg>
          </EmptyIcon>
          <EmptyText>표시할 고객 DB가 없습니다.</EmptyText>
        </EmptyBox>
      ) : (
        rows.map((r) => (
          <TableRow key={r.id}>
            <TdCell $flex={1}>{r.date}</TdCell>
            <TdCell $flex={0.7}>{r.name}</TdCell>
            <TdCell $flex={1.5}>{formatPhone(r.phone)}</TdCell>
            <TdCell $flex={0.7}>
              <StatusStack>
                <CategorySub>{r.category}</CategorySub>
                <StatusLine>{r.status}</StatusLine>
              </StatusStack>
            </TdCell>
          </TableRow>
        ))
      )}
    </Container>
  );
};

export default CustomerTrackHistory;

// ───── 스타일 ─────
const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const SectionDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const GridWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.sm};
`;

const StatusCell = styled.div`
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  text-align: center;
  min-height: 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const CellLabel = styled.span`
  font-size: 10px;
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-bottom: 2px;
  white-space: nowrap;
`;

const CellValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const TableHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ThCell = styled.div`
  flex: ${({ $flex }) => $flex ?? 1};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
`;

const TableRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const TdCell = styled.div`
  flex: ${({ $flex }) => $flex ?? 1};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
`;

const StatusStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.2;
`;

const CategorySub = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const StatusLine = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
`;

const EmptyIcon = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;
